'use client';

import React from 'react';
import Box from '../../../atoms/Box/Box';
import Dropdown from '../../../atoms/Dropdown/Dropdown';

type LocaleGroup = {
  groupLabel: string;
  data: { value: string; label: string }[];
};

interface LanguageSelectorProps {
  theme?: 'light' | 'dark';
  selectedLanguage: string;
  onLanguageSelect: (locale: string) => void;
  /** Override the default locale options. When provided, replaces the built-in grouped list. */
  localeOptions?: LocaleGroup[];
  /** i18n labels */
  labels?: {
    /** Region label (default: "Region") */
    region?: string;
  };
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  theme = 'dark',
  selectedLanguage,
  onLanguageSelect,
  localeOptions,
  labels,
}) => {
  // Language options with grouping by region
  const defaultLanguageOptions = [
    {
      groupLabel: 'North America',
      data: [
        { value: 'en-us', label: 'United States - English' },
        { value: 'en-ca', label: 'Canada - English' },
        { value: 'fr-ca', label: 'Canada - Français' },
      ],
    },
    {
      groupLabel: 'Europe',
      data: [
        { value: 'da-dk', label: 'Danmark - Dansk' },
        { value: 'de-de', label: 'Deutschland - Deutsch' },
        { value: 'de-ch', label: 'Schweiz - Deutsch' },
        { value: 'fr-fr', label: 'France - Français' },
        { value: 'fr-ch', label: 'Suisse - Français' },
        { value: 'nl-nl', label: 'Nederland - Nederlands' },
        { value: 'nb-no', label: 'Norge - Norsk' },
        { value: 'en-be', label: 'Belgium - English' },
        { value: 'nl-be', label: 'België - Nederlands' },
        { value: 'fr-be', label: 'Belgique - Français' },
      ],
    },
    {
      groupLabel: 'Middle East',
      data: [
        { value: 'en-sa', label: 'KSA - English' },
        { value: 'ar-sa', label: 'المملكة العربية السعودية - العربية' },
        { value: 'en-ae', label: 'UAE - English' },
        { value: 'ar-ae', label: 'الإمارات العربية المتحدة - العربية' },
      ],
    },
  ];

  const languageOptions = localeOptions ?? defaultLanguageOptions;

  return (
    <Box>
      <Dropdown
        theme={theme}
        variant="list"
        size="medium"
        name="language-selector"
        label={labels?.region ?? 'Region'}
        options={languageOptions}
        selectedOption={selectedLanguage}
        placeholder={labels?.region ?? 'Region'}
        handleSelect={onLanguageSelect}
        gaComponent="sidebar-language-selector"
        logEvents={false}
      />
    </Box>
  );
};

LanguageSelector.displayName = 'LanguageSelector';

export default LanguageSelector;
